import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";

interface Org {
  id: string;
  name: string;
}

interface OrgContextType {
  orgs: Org[];
  currentOrg: Org | null;
  setCurrentOrg: (org: Org) => void;
  isLoading: boolean;
  handleLeaveOrg: (orgId: string) => void;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export const OrgProvider = ({ children }: { children: React.ReactNode }) => {
  const { idToken } = useAuth();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [currentOrg, setCurrentOrgState] = useState<Org | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setCurrentOrg = React.useCallback(
    (org: Org) => {
      setCurrentOrgState(org);
      if (idToken) {
        const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";
        fetch(`${API_BASE}/me/preferences`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ lastOrgId: org.id }),
        }).catch(() => {});
      }
    },
    [idToken]
  );

  const handleLeaveOrg = (orgId: string) => {
    setOrgs((prev) => prev.filter((org) => org.id !== orgId));
    if (currentOrg && currentOrg.id === orgId) {
      // If the current org was removed, select a new one or clear
      const newOrgs = orgs.filter((org) => org.id !== orgId);
      if (newOrgs.length > 0) {
        setCurrentOrg(newOrgs[0]);
      } else {
        setCurrentOrgState(null);
        if (idToken) {
          const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";
          fetch(`${API_BASE}/me/preferences`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ lastOrgId: null }),
          }).catch(() => {});
        }
      }
    }
  };

  useEffect(() => {
    const fetchOrgs = async () => {
      if (!idToken) return;
      const API_BASE =
        (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";
      try {
        const res = await fetch(`${API_BASE}/orgs`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!res.ok) throw new Error("Failed to fetch orgs");
        const data: Org[] = await res.json();
        setOrgs(data);

        // Fetch preferences and set last used org
        let pref: { lastOrgId: string | null } | null = null;
        try {
          const prefRes = await fetch(`${API_BASE}/me/preferences`, {
            headers: { Authorization: `Bearer ${idToken}` },
          });
          if (prefRes.ok) pref = await prefRes.json();
        } catch {}
        if (pref?.lastOrgId) {
          const found = data.find(o => o.id === pref.lastOrgId);
          if (found) {
            setCurrentOrg(found);
          } else if (data.length > 0) {
            setCurrentOrg(data[0]);
          }
        } else if (data.length > 0) {
          setCurrentOrg(data[0]);
        }
      } catch (err) {
        console.error("[OrgContext] Org fetch failed", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrgs();
  }, [idToken, setCurrentOrg]);

  console.log("✅ OrgProvider rendered", { currentOrg, orgs });

  return (
    <OrgContext.Provider value={{ orgs, currentOrg, setCurrentOrg, isLoading, handleLeaveOrg }}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = (): OrgContextType => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be used within OrgProvider");
  return ctx;
};
